import LegalPage, { H2, H3, Ph } from "../components/LegalPage";
import { Link } from "react-router-dom";
import { openCookieSettings } from "../components/CookieConsent";

const EMAIL = "sugaspicebakery01@gmail.com";

export default function Privacy() {
  return (
    <LegalPage title="Privacy Policy" updated="June 2026">
      <p>
        This Privacy Policy explains how SugaSpice Bakery &amp; Kitchen ("SugaSpice", "we", "us")
        collects and uses your personal information when you use our website
        (<Link to="/" className="text-[#C8102E] underline">sugaspice.uk</Link>), contact us, enquire
        about wholesale supply, or sign up to our newsletter. We are committed to protecting your
        privacy and handling your data in line with UK data protection law (the UK GDPR and the Data
        Protection Act 2018).
      </p>

      <H2>Who we are</H2>
      <p>
        SugaSpice Bakery &amp; Kitchen is a bakery, kitchen and coffee house based at 10 Moore's Walk,
        St Neots, Cambridgeshire, PE19 1AG.
      </p>
      <p>
        We are the "data controller" responsible for your personal information. Our legal/registered
        details are: <Ph>SugaSpice full legal/registered business name</Ph>,{" "}
        <Ph>business structure — Ltd company or sole trader</Ph>,{" "}
        <Ph>company registration number (if a limited company)</Ph>,{" "}
        <Ph>registered office address</Ph>.
      </p>
      <p>
        If you have any questions about this policy or your data, contact us at{" "}
        <a href={`mailto:${EMAIL}`} className="text-[#C8102E] underline">{EMAIL}</a> or by post at the
        address above.
      </p>

      <H2>What information we collect</H2>
      <p>We only collect information you choose to give us through the website. Depending on how you use the site, this may include:</p>
      <H3>Contact form</H3>
      <ul className="list-disc pl-6 space-y-1">
        <li>Your name</li>
        <li>Your email address</li>
        <li>Your telephone number (optional)</li>
        <li>The subject and content of your message</li>
      </ul>
      <H3>Wholesale enquiry form</H3>
      <ul className="list-disc pl-6 space-y-1">
        <li>Your business name and your contact name</li>
        <li>Your email address and telephone number (telephone optional)</li>
        <li>Business type, location, products you're interested in, expected volumes and any message</li>
      </ul>
      <H3>Newsletter signup</H3>
      <ul className="list-disc pl-6 space-y-1">
        <li>Your email address</li>
        <li>The date/time you subscribed and which form you used</li>
        <li>A record that you consented to marketing emails</li>
      </ul>
      <H3>Website usage data (only with your consent)</H3>
      <p>
        If you accept analytics and/or marketing cookies, we collect limited information about how you
        use the site (such as pages viewed and approximate location derived from your IP address) and,
        for advertising measurement, interactions tracked by the Meta (Facebook) Pixel. See our{" "}
        <Link to="/cookies" className="text-[#C8102E] underline">Cookie Policy</Link> for the detail.
        We do <strong>not</strong> use session recording.
      </p>

      <H2>How we use your information and our legal basis</H2>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border border-[#D1C9BE]/70 rounded-lg">
          <thead className="bg-[#1A1A1A] text-[#F7F5F0]">
            <tr><th className="text-left p-3">What we do</th><th className="text-left p-3">Legal basis (UK GDPR)</th></tr>
          </thead>
          <tbody className="divide-y divide-[#D1C9BE]/60">
            <tr><td className="p-3">Respond to contact enquiries</td><td className="p-3">Legitimate interests (replying to you)</td></tr>
            <tr><td className="p-3">Handle and follow up wholesale enquiries</td><td className="p-3">Legitimate interests / steps to enter a contract</td></tr>
            <tr><td className="p-3">Send marketing emails (newsletter)</td><td className="p-3">Consent (you can withdraw at any time)</td></tr>
            <tr><td className="p-3">Analytics &amp; advertising cookies</td><td className="p-3">Consent</td></tr>
            <tr><td className="p-3">Keep basic records and meet legal obligations</td><td className="p-3">Legitimate interests / legal obligation</td></tr>
          </tbody>
        </table>
      </div>
      <p>
        Submitting a contact or wholesale enquiry does <strong>not</strong> sign you up to marketing.
        We keep business enquiries and marketing consent separate.
      </p>

      <H2>Who we share it with</H2>
      <p>We do not sell your personal information. We share it only with service providers that help us run the website and business, including:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li><strong>Our website &amp; hosting provider</strong> — stores website data and form submissions.</li>
        <li><strong>PostHog</strong> — website analytics (only if you accept analytics cookies).</li>
        <li><strong>Meta Platforms (Facebook/Instagram)</strong> — advertising measurement via the Meta Pixel (only if you accept marketing cookies).</li>
        <li><strong>Google &amp; Fontshare</strong> — serve the website's fonts; your IP address is processed to deliver them.</li>
        <li><strong>Flipdish</strong> — our online ordering partner. If you order online you leave our website and Flipdish's own privacy policy applies.</li>
        <li><Ph>Any email marketing provider you use to send the newsletter, if applicable</Ph></li>
      </ul>
      <p>Some of these providers may process data outside the UK; where they do, appropriate safeguards (such as UK/EU approved transfer mechanisms) are relied upon.</p>

      <H2>How long we keep it</H2>
      <ul className="list-disc pl-6 space-y-1">
        <li>Contact enquiries: <Ph>retention period — suggested up to 24 months</Ph></li>
        <li>Wholesale enquiries / customer records: <Ph>retention period — suggested up to 6 years for business/accounting records</Ph></li>
        <li>Newsletter subscribers: until you unsubscribe, then removed within <Ph>e.g. 30 days</Ph></li>
        <li>Analytics data: as set by our analytics provider's retention settings</li>
      </ul>

      <H2>Your rights</H2>
      <p>Under UK data protection law you have the right to:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li>Access the personal information we hold about you</li>
        <li>Ask us to correct inaccurate information</li>
        <li>Ask us to delete your information where appropriate</li>
        <li>Object to, or ask us to restrict, our processing</li>
        <li>Withdraw consent at any time (e.g. unsubscribe from marketing)</li>
        <li>Ask for a copy of your data in a portable format, where applicable</li>
      </ul>
      <p>
        To exercise any of these rights, email us at{" "}
        <a href={`mailto:${EMAIL}`} className="text-[#C8102E] underline">{EMAIL}</a>. You can also
        change your cookie choices at any time via{" "}
        <button onClick={openCookieSettings} className="text-[#C8102E] underline">Cookie Settings</button>.
      </p>

      <H2>Marketing opt-out</H2>
      <p>
        Every marketing email we send includes an unsubscribe link, and you can email us to opt out at
        any time. We'll stop sending marketing as soon as you ask.
      </p>

      <H2>Complaints</H2>
      <p>
        We'd always prefer to resolve any concern directly, so please contact us first. You also have
        the right to complain to the Information Commissioner's Office (ICO), the UK data protection
        regulator, at <a href="https://ico.org.uk/make-a-complaint/" target="_blank" rel="noopener noreferrer" className="text-[#C8102E] underline">ico.org.uk</a> or by calling 0303 123 1113.
      </p>
      <p className="text-sm text-[#5C5C5C]">
        Our ICO registration (Data Protection fee) number: <Ph>ICO registration number, if registered</Ph>.
      </p>

      <H2>Changes to this policy</H2>
      <p>We may update this policy from time to time. The latest version will always be on this page with the date it was last updated.</p>
    </LegalPage>
  );
}
